/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import cstmTable from '../../../../share/components/vue/PaginationTable';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import error from '../../../../common/nodejs/errors';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  SUCCESS: 'success',
  FAILED: 'failed'
};

export default {
  name: 'requirement066',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      bodyItems: [],
      tableItems: [],
      modal_requirement_066: false
    };
  },

  props: {
    workSupportId: { type: Number, default: '', required: true }
  },
  components: {
    'cstm-table': cstmTable
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        default:
          return 'secondary';
      }
    },

    systemTableColumns() {

      this.tableItems = [
        {
          name: 'itemName',
          label: 'individual.label_table_applied_model'
        },
        {
          name: 'itemValue',
          label: 'individual.label_table_option_code'
        }
      ];
      return this.tableItems;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_066 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_066 = false;
    },

    // API実行-------------------------------------------
    
    /** バリアントデータ取得 */
    async getVariantData() {
      try {
        // 車両通信中を表示
        this.isLoading = true;

        // EUCキーの取得
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        // データ読出し (要求)
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'start'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // console.log(response);
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 成功
              // 表示するデータをセット
              await this.setVariantData(response.data.user_data.result_data);
              break;
            case config.STATUS_WSP_FAILED:
              switch (response.data.async_code) {
                case error.WSP_GENERAL_ERROR:
                  // データ読出し失敗
                  this.showUnexpectedError();
                  break;
                case error.WSP_066_GET_VARIANTDATA_FAILED:
                  // 全DIDの応答値と一致するデータがない
                  // またはバリアントデータ読出し失敗
                  // 機能終了
                  this.endProcessing();
                  break;
                default:
                  // 該当のエラーコードが存在しない場合、汎用エラー
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    // 表示内容の設定-------------------------------------------

    /**
     * バリアントデータを表示するためのデータセット
     * @param {*} tempData バックエンドから受け取ったデータ
     */
    async setVariantData(tempData) {
      const showData = [];

      // 正常終了表示に遷移
      this.phase = PHASE.SUCCESS;

      // データセット
      this.$nextTick(() => {
        tempData.forEach(element => {
          // リスト表示項目の設定
          const item =
          {
            itemName: element.applied_model,
            itemValue: element.op_code
          };

          // バックエンドから受け取った全データをテーブル表示に使用できる形にまとめる
          showData.push(item);
        });

        // テーブルに表示する内容確定
        this.bodyItems = showData;
      });
      return;
    },
    
    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 終了時の処理 */
    async endProcessing() {
      this.closeWizard();

      // 初期画面に戻す
      this.phase = PHASE.START;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    }
  },

  async mounted() {
    this.init();
    this.getVariantData();
  }
};
